<template>
  <q-btn
    :color="color"
    :style="styles"
    :text-color="computedTextColor"
    :size="isXss ? 'xs' : size"
    :title="null"
    :disable="disableComputed || readonlyComputed"
    :class="{ xss: isXss }"
  >
    <slot />
    <q-tooltip v-if="title" class="bg-primary">
      {{ title }}
    </q-tooltip>
  </q-btn>
</template>

<script setup>
import { computed, useAttrs } from "vue"

import { useRoute } from "vue-router"

import { useFieldsetState } from "@/composables/useFieldsetState.js"

const route = useRoute()

const props = defineProps({
  styles: {
    type: Object,
    default: () => null
  },
  title: {
    type: String,
    default: null
  },
  textColor: {
    type: String,
    default: null
  },
  routeMatchAccentColor: {
    type: String,
    default: null
  },
  size: {
    type: String,
    default: null
  },
  disable: Boolean,
  formStateInheritanceDisabled: Boolean
})

// Buttons follow the surrounding form/card disabled state (injected), and can opt out via
// form-state-inheritance-disabled - same mechanism as the input components. A button cannot be
// "readonly", so a readonly form/card locks it by disabling it.
const { disableComputed, readonlyComputed } = useFieldsetState(props)

const isXss = computed(() => props.size === "xss")

const attrs = useAttrs()

const color = computed(() => (props.styles ? null : "dark"))

const routeMatchesToProp = computed(() => {
  if (route.path === attrs.to) {
    return true
  }
  const filteredParams = Object.fromEntries(Object.entries(route.params || {}).filter(([_, value]) => value !== ""))
  return route.name === attrs.to?.name && JSON.stringify(filteredParams) === JSON.stringify(attrs.to?.params || {})
})

const computedTextColor = computed(() => {
  if (props.routeMatchAccentColor && routeMatchesToProp.value) {
    return props.routeMatchAccentColor
  }

  return props.textColor
})
</script>

<style lang="scss" scoped>
.xss {
  min-height: 20px;
  min-width: 20px;
  padding: 0 3px;

  :deep(.q-icon) {
    font-size: 14px;
  }
}
</style>
